import type { EChartsOption } from "echarts";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CohortBlock, MonitoringRelease } from "../data/monitoring";
import { Chart } from "./Chart";
import { InfoTip } from "./InfoTip";
import { ChartData } from "./UI";
import { buildCohortComparisonRows, sortCohortComparisonRows, type CohortComparisonRow } from "./cohortComparison";

const FAMILY_LABELS: Record<string, string> = {
  status: "Status",
  lapsed_group: "Lapsed Group",
  primary_game_flag: "Primary Game",
};
const pct = (value: number) => `${(value * 100).toFixed(1)}%`;
const pp = (value: number) => `${value >= 0 ? "+" : ""}${(value * 100).toFixed(1)}pp`;

const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value));
const blend = (from: [number, number, number], to: [number, number, number], amount: number) => `rgb(${from.map((channel, index) => Math.round(channel + (to[index] - channel) * amount)).join(", ")})`;

function indexColor(value: number) {
  const intensity = clamp(Math.abs(value - 1) / 0.4, 0, 1);
  if (value >= 1) return blend([174, 184, 201], [99, 89, 215], intensity);
  return blend([174, 184, 201], [224, 79, 99], intensity);
}

export function cohortIndexDomain(indexValues: number[]) {
  const deviation = Math.max(0.15, ...indexValues.map((value) => Math.abs(value - 1)));
  const labelBuffer = 0.16;
  const minimum = Math.max(0, Math.min(0.6, 1 - deviation * 1.14) - labelBuffer);
  const maximum = Math.max(1.4, 1 + deviation * 1.14) + labelBuffer;
  return { minimum: Number(minimum.toFixed(2)), maximum: Number(maximum.toFixed(2)) };
}

function CohortIndexBarChart({
  title,
  metric,
  rows,
  selectedTheme,
  onSelect,
}: {
  title: string;
  metric: "share" | "negativity";
  rows: CohortComparisonRow[];
  selectedTheme: string | null;
  onSelect: (index: number) => void;
}) {
  const indexValue = (row: CohortComparisonRow) => metric === "share" ? row.shareIndex : row.negativityIndex;
  const indexValues = rows.map(indexValue).filter(Number.isFinite);
  // Keep the counterpart's 1.00x reference in the visual centre so the
  // direction and size of every subgroup difference are immediately legible.
  // Keep a shared standard index window across both panels and reserve equal
  // room for labels outside below- and above-parity bar endpoints.
  const { minimum, maximum } = cohortIndexDomain(indexValues);
  const option: EChartsOption = {
    animation: false,
    // Reserve a dedicated gutter between wrapped theme labels and the
    // left-facing index labels on below-parity bars.
    grid: { left: 242, right: 26, top: 36, bottom: 28 },
    tooltip: {
      trigger: "item",
      confine: true,
      formatter: (params: unknown) => {
        const index = (params as { dataIndex?: number }).dataIndex ?? 0;
        const row = rows[index];
        return `<b>${row.theme}</b><br/>${row.cohortLabel}: ${indexValue(row).toFixed(2)}x index<br/>${row.counterpartLabel}: 1.00x baseline`;
      },
    },
    xAxis: {
      type: "value", min: minimum, max: maximum,
      axisLabel: { color: "var(--muted)", fontSize: 12, formatter: (value: number) => `${value.toFixed(1)}x` },
      axisLine: { lineStyle: { color: "var(--line)" } },
      splitLine: { lineStyle: { color: "var(--divider)", type: "dashed" } },
      axisPointer: { show: false },
    },
    yAxis: {
      type: "category", inverse: true, data: rows.map((row) => row.theme),
      axisLabel: { show: true, color: "var(--ink)", fontSize: 13, fontWeight: 700, width: 222, overflow: "break", lineHeight: 16, align: "right" },
      axisTick: { show: false }, axisLine: { show: false }, splitLine: { show: false },
    },
    series: [
      {
        type: "custom",
        data: rows.map((row, index) => [indexValue(row), index]),
        renderItem: (_params, api) => {
          const value = Number(api.value(0));
          const rowIndex = Number(api.value(1));
          const row = rows[rowIndex];
          const baseline = api.coord([1, rowIndex]);
          const endpoint = api.coord([value, rowIndex]);
          const barHeight = 12;
          const x = Math.min(baseline[0], endpoint[0]);
          const width = Math.max(2, Math.abs(endpoint[0] - baseline[0]));
          const isPositive = value >= 1;
          const color = indexColor(value);
          return {
            type: "group",
            children: [
              { type: "rect", shape: { x, y: endpoint[1] - barHeight / 2, width, height: barHeight, r: 4 }, style: { fill: color, stroke: row?.theme === selectedTheme ? "#ff1e00" : color, lineWidth: row?.theme === selectedTheme ? 2 : 0 } },
              { type: "text", style: { x: endpoint[0] + (isPositive ? 7 : -7), y: endpoint[1], text: `${value.toFixed(2)}x`, textAlign: isPositive ? "left" : "right", textVerticalAlign: "middle", fill: color, font: "700 12px Geist, sans-serif" } },
            ],
          };
        },
        silent: false,
      },
    ],
  };

  return <section className="cohort-dumbbell__panel"><div className="cohort-dumbbell__panel-heading"><span>{title}</span><small>{metric === "share" ? "Share Index" : "Negativity Index"}: Subgroup ÷ All Others</small></div><div className="cohort-dumbbell__legend">1.00x = Parity with {rows[0]?.counterpartLabel}</div><Chart option={option} ariaLabel={`${title} Cohort Index Comparison`} height={Math.max(236, rows.length * 32 + 80)} onPointSelect={onSelect} selectAnySeries /></section>;
}

export function CohortDumbbellComparison({ cohorts }: { cohorts: MonitoringRelease["cohorts"] }) {
  const families = useMemo(() => Object.entries(cohorts).filter(([, section]) => section.available && section.blocks.length), [cohorts]);
  const [familyKey, setFamilyKey] = useState(() => families[0]?.[0] ?? "");
  const activeFamily = families.find(([key]) => key === familyKey) ?? families[0];
  const blocks = activeFamily?.[1].blocks ?? [];
  const [cohortValue, setCohortValue] = useState(() => blocks[0]?.cohort_value ?? "");
  const activeBlock = blocks.find((block) => block.cohort_value === cohortValue) ?? blocks[0];
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const selectedRow = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    setCohortValue(blocks[0]?.cohort_value ?? "");
    setSelectedTheme(null);
  }, [activeFamily?.[0]]);

  useEffect(() => {
    if (!selectedTheme || !selectedRow.current) return;
    const frame = window.requestAnimationFrame(() => selectedRow.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
    return () => window.cancelAnimationFrame(frame);
  }, [selectedTheme]);

  if (!activeBlock) return <p className="muted-copy">Cohort comparisons are unavailable for this release.</p>;

  const allRows = buildCohortComparisonRows(activeBlock);
  const shareRows = sortCohortComparisonRows(allRows, "share");
  const negativityRows = sortCohortComparisonRows(allRows, "negativity");
  const selectTheme = (rows: CohortComparisonRow[], index: number) => setSelectedTheme((current) => current === rows[index]?.theme ? null : rows[index]?.theme ?? null);
  const cohortSize = activeBlock.cards.find((card) => card.label.toLowerCase() === "cohort size")?.value;
  const headline = cohortSize
    ? activeBlock.headline.replace(activeBlock.cohort_value, `${activeBlock.cohort_value} (${cohortSize})`)
    : activeBlock.headline;

  return <article className="cohort-dumbbell">
    <div className="cohort-dumbbell__controls">
      <div className="segmented-control" role="group" aria-label="Cohort family">
        {families.map(([key]) => <button type="button" key={key} className={key === activeFamily?.[0] ? "is-active" : ""} onClick={() => setFamilyKey(key)}>{FAMILY_LABELS[key] ?? key}</button>)}
      </div>
      {blocks.length > 1 && <div className="segmented-control" role="group" aria-label="Cohort subgroup">
        {blocks.map((block) => <button type="button" key={block.cohort_value} className={block.cohort_value === activeBlock.cohort_value ? "is-active" : ""} onClick={() => { setCohortValue(block.cohort_value); setSelectedTheme(null); }}>{block.cohort_value}</button>)}
      </div>}
    </div>
    <div className="cohort-dumbbell__summary"><div><div className="cohort-dumbbell__eyebrow"><span className="visual-kicker">Selected Comparison</span></div><h3>{activeBlock.cohort_value} <i /> {activeBlock.counterpart_label ?? "All Other Observed Members"}</h3><p>{headline}</p></div><InfoTip label="How to read cohort comparisons">Each index compares the selected subgroup with all other observed members. 1.00× means parity; values above or below it indicate higher or lower representation. Click a graph bar to open and highlight that theme’s detailed row. These comparisons describe observed differences, not causality.</InfoTip></div>
    <div className="cohort-dumbbell__charts"><CohortIndexBarChart title="Theme Share" metric="share" rows={shareRows} selectedTheme={selectedTheme} onSelect={(index) => selectTheme(shareRows, index)} /><CohortIndexBarChart title="Negativity Rate" metric="negativity" rows={negativityRows} selectedTheme={selectedTheme} onSelect={(index) => selectTheme(negativityRows, index)} /></div>
    <ChartData label="View Cohort Theme Detail" open={selectedTheme !== null} onToggle={(open) => { if (!open) setSelectedTheme(null); }}>
      <div className="data-table-wrap"><table className="data-table"><caption className="sr-only">Cohort comparison detail</caption><thead><tr><th>Theme</th><th>{activeBlock.cohort_value} Share</th><th>{activeBlock.counterpart_label ?? "Counterpart"} Share</th><th>Share Difference</th><th>{activeBlock.cohort_value} Negativity</th><th>{activeBlock.counterpart_label ?? "Counterpart"} Negativity</th><th>Negativity Difference</th></tr></thead><tbody>{allRows.map((row) => <tr key={row.theme} ref={row.theme === selectedTheme ? selectedRow : undefined} className={row.theme === selectedTheme ? "is-chart-selection" : undefined}><td><b>{row.theme}</b></td><td>{pct(row.cohortShare)}</td><td>{pct(row.counterpartShare)}</td><td>{pp(row.shareDelta)}</td><td>{pct(row.cohortNegativity)}</td><td>{pct(row.counterpartNegativity)}</td><td>{pp(row.negativityDelta)}</td></tr>)}</tbody></table></div>
    </ChartData>
  </article>;
}
